/**
 * Phase 6D — Victorian Selective Entry Examination Centre (Student).
 *
 * 3 views:
 *   • catalogue: list of 90 exams + filters + leaderboard + achievements
 *   • intro: instructions + Start button
 *   • runner: timer, navigator, flag, autosave, submit
 *   • results: AI report, breakdowns, adaptive recommendations
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, Clock, Trophy, Flag, ChevronRight, ChevronLeft,
  Send, CheckCircle2, Star, Flame, Target, Rocket, BookOpen, Sparkles,
  Award, Users, TrendingUp,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const ICON_MAP = { trophy: Trophy, star: Star, flame: Flame, target: Target, rocket: Rocket, 'book-open': BookOpen };

const ExamCentrePage = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('catalogue');
  const [catalogue, setCatalogue] = useState({ items: [], summary: null });
  const [achievements, setAchievements] = useState({ earned: [], locked: [] });
  const [leaderboard, setLeaderboard] = useState(null);
  const [filter, setFilter] = useState('all');
  const [intro, setIntro] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [c, a, lb] = await Promise.all([
        ax.get('/api/exam/centre'),
        ax.get('/api/me/student/exam/achievements'),
        ax.get('/api/exam/leaderboard?period=weekly'),
      ]);
      setCatalogue(c.data); setAchievements(a.data); setLeaderboard(lb.data);
    } catch (e) { toast.error('Could not load Exam Centre'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openIntro = async (slug) => {
    try {
      const r = await ax.get(`/api/exam/${slug}`);
      setIntro(r.data); setView('intro');
    } catch (e) { toast.error('Could not open exam'); }
  };
  const startExam = async () => {
    try {
      const r = await ax.post(`/api/exam/${intro.exam.slug}/start`);
      setAttempt(r.data.attempt); setView('runner');
    } catch (e) { toast.error('Could not start exam'); }
  };
  const onSubmit = (data) => { setResult(data); setView('results'); load(); };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><Loader2 className="h-7 w-7 animate-spin text-amber-700"/></div>;

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="exam-centre">
      <header className="bg-black text-white px-6 py-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <button onClick={() => navigate('/student/dashboard')} className="text-[10px] uppercase tracking-[3px] text-[#D3D86F] inline-flex items-center gap-1 hover:underline" data-testid="exam-back">
            <ArrowLeft className="h-3 w-3"/> Student Portal
          </button>
          <h1 className="text-xl font-bold mt-1">Examination Centre · Victorian Selective Entry</h1>
        </div>
        <button onClick={() => navigate('/student/learning')} className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full" data-testid="back-to-learning">Learning Centre →</button>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {view === 'catalogue' && <Catalogue catalogue={catalogue} achievements={achievements} leaderboard={leaderboard} filter={filter} setFilter={setFilter} onOpen={openIntro}/>}
        {view === 'intro' && intro && <IntroScreen intro={intro} onStart={startExam} onCancel={() => { setIntro(null); setView('catalogue'); }}/>}
        {view === 'runner' && attempt && <Runner attempt={attempt} onSubmit={onSubmit} onExit={() => { setAttempt(null); setView('catalogue'); }}/>}
        {view === 'results' && result && <Results result={result} onClose={() => { setResult(null); setView('catalogue'); }}/>}
      </main>
    </div>
  );
};

const Catalogue = ({ catalogue, achievements, leaderboard, filter, setFilter, onOpen }) => {
  const items = catalogue.items.filter(i => filter === 'all' || i.track === filter);
  const s = catalogue.summary || {};
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Total exams" value={s.total_exams || 0} accent={false}/>
        <Stat label="Maths" value={(s.by_track || {}).mathematics || 0}/>
        <Stat label="English" value={(s.by_track || {}).english || 0}/>
        <Stat label="Completed" value={s.completed || 0} accent={true}/>
      </div>

      {/* Achievements */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4" data-testid="achievements-panel">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-bold flex items-center gap-2"><Award className="h-4 w-4 text-amber-600"/> Achievements ({achievements.earned.length}/{achievements.earned.length + achievements.locked.length})</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {achievements.earned.map(a => {
            const Icon = ICON_MAP[a.icon] || Trophy;
            return <div key={a.slug} className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 flex items-center gap-2" data-testid={`badge-${a.slug}`}>
              <Icon className="h-4 w-4 text-amber-600 fill-amber-200"/>
              <div>
                <div className="text-xs font-bold text-gray-900">{a.title}</div>
                <div className="text-[10px] text-gray-500">{a.description}</div>
              </div>
            </div>;
          })}
          {achievements.locked.slice(0,6).map(a => {
            const Icon = ICON_MAP[a.icon] || Trophy;
            return <div key={a.slug} className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 flex items-center gap-2 opacity-60" data-testid={`locked-${a.slug}`}>
              <Icon className="h-4 w-4 text-gray-400"/>
              <div>
                <div className="text-xs font-bold text-gray-500">{a.title}</div>
                <div className="text-[10px] text-gray-400">{a.description}</div>
              </div>
            </div>;
          })}
        </div>
      </div>

      {/* Leaderboard */}
      {leaderboard && leaderboard.items?.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-4" data-testid="leaderboard-panel">
          <div className="text-sm font-bold flex items-center gap-2 mb-2"><Users className="h-4 w-4"/> Weekly Leaderboard ({leaderboard.total_participants})</div>
          <ol className="space-y-1.5">
            {leaderboard.items.slice(0,5).map((row,i) => (
              <li key={i} className="flex items-center justify-between text-xs">
                <span><strong>#{row.rank}</strong> {row.display_name} <em className="text-gray-500">· {row.exam_title}</em></span>
                <span className="font-bold text-emerald-700">{row.score_pct}%</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Exam catalogue */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold">Examinations</div>
          <div className="flex gap-1">
            {['all','mathematics','english','combined'].map(f => (
              <button key={f} onClick={()=>setFilter(f)} className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full ${filter===f?'bg-amber-600 text-white':'bg-gray-100 hover:bg-gray-200'}`} data-testid={`filter-${f}`}>{f}</button>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5 max-h-[600px] overflow-y-auto pr-1" data-testid="exam-catalogue-list">
          {items.map(e => (
            <button key={e.slug} onClick={()=>onOpen(e.slug)} className="text-left border border-gray-200 rounded-xl p-3 hover:border-amber-400 hover:bg-amber-50/40 transition relative" data-testid={`exam-${e.slug}`}>
              <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">{e.track}</div>
              <div className="text-sm font-bold text-gray-900 mt-0.5">{e.title}</div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-1 flex items-center gap-2">
                <Clock className="h-2.5 w-2.5"/> {e.duration_minutes} min · {e.num_questions} q
                {e.my_best_pct !== null && e.my_best_pct !== undefined && (
                  <span className={`font-bold ${e.my_best_pct>=80?'text-emerald-700':e.my_best_pct>=60?'text-amber-700':'text-rose-700'}`}>· Best {e.my_best_pct}%</span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const Stat = ({label, value, accent}) => (
  <div className={`rounded-xl p-3 ${accent?'bg-[#D3D86F] text-black':'bg-white border border-gray-200'}`}>
    <div className={`text-[10px] uppercase tracking-wider font-bold ${accent?'text-black/70':'text-gray-500'}`}>{label}</div>
    <div className="text-2xl font-bold mt-0.5">{value}</div>
  </div>
);

const IntroScreen = ({ intro, onStart, onCancel }) => (
  <div className="bg-white rounded-2xl border border-amber-200 p-6 max-w-3xl mx-auto" data-testid="exam-intro">
    <div className="text-[10px] uppercase tracking-[3px] text-amber-700 font-bold">Examination Centre</div>
    <h2 className="text-2xl font-bold text-gray-900 mt-1">{intro.exam.title}</h2>
    <div className="text-xs text-gray-500 mt-1">{intro.exam.track} · {intro.exam.duration_minutes} min · {intro.exam.num_questions} questions</div>
    <p className="text-sm text-gray-700 mt-3">{intro.exam.description}</p>
    <div className="mt-4 bg-gray-50 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Instructions</div>
      <ul className="text-sm text-gray-800 list-disc pl-5 space-y-1">
        {intro.instructions.map((s,i) => <li key={i}>{s}</li>)}
      </ul>
    </div>
    <div className="mt-4 flex gap-2 justify-end">
      <button onClick={onCancel} className="text-xs px-4 py-2 border rounded-full" data-testid="exam-cancel">Cancel</button>
      <button onClick={onStart} className="bg-amber-600 hover:bg-amber-700 text-white px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2" data-testid="exam-start"><Rocket className="h-3 w-3"/> Start Exam</button>
    </div>
  </div>
);

const Runner = ({ attempt, onSubmit, onExit }) => {
  const [answers, setAnswers] = useState(attempt.answers || {});
  const [flagged, setFlagged] = useState(attempt.flagged || {});
  const [cur, setCur] = useState(0);
  const total = attempt.duration_minutes * 60;
  const startedAt = useRef(Date.now());
  const [timeLeft, setTimeLeft] = useState(total);
  const [busy, setBusy] = useState(false);
  const questions = attempt.questions;

  // Countdown
  useEffect(() => {
    const id = setInterval(() => setTimeLeft(t => {
      if (t <= 1) { clearInterval(id); return 0; }
      return t - 1;
    }), 1000);
    return () => clearInterval(id);
  }, []);
  // Auto-submit
  useEffect(() => { if (timeLeft === 0) submit(); }, [timeLeft]);

  // Auto-save every 20s
  useEffect(() => {
    const id = setInterval(() => {
      ax.patch(`/api/exam/attempt/${attempt.id}/autosave`, { answers, flagged }).catch(()=>{});
    }, 20000);
    return () => clearInterval(id);
  }, [attempt.id, answers, flagged]);

  const submit = async () => {
    if (!window.confirm('Submit exam now? You cannot change answers after submitting.')) return;
    setBusy(true);
    try {
      // Final autosave then submit
      await ax.patch(`/api/exam/attempt/${attempt.id}/autosave`, { answers, flagged }).catch(()=>{});
      const r = await ax.post(`/api/exam/attempt/${attempt.id}/submit`);
      onSubmit(r.data);
    } catch (e) { toast.error('Submit failed — retry shortly'); }
    finally { setBusy(false); }
  };
  const min = Math.floor(timeLeft / 60), sec = timeLeft % 60;
  const warning = timeLeft <= 60;
  const q = questions[cur];
  const flag = () => setFlagged(f => ({ ...f, [q.id]: !f[q.id] }));
  const skip = () => setCur(Math.min(questions.length - 1, cur + 1));
  const answeredCount = Object.keys(answers).filter(k=>answers[k]!==undefined && answers[k]!=='').length;

  return (
    <div className="bg-white rounded-2xl border border-amber-200 p-5" data-testid="exam-runner">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <button onClick={()=>{ if(window.confirm('Exit exam? Your answers are auto-saved and you can resume later.')) onExit(); }} className="text-[10px] uppercase tracking-[2px] text-amber-700 inline-flex items-center gap-1 hover:underline" data-testid="exam-exit"><ArrowLeft className="h-3 w-3"/> Exit (saved)</button>
        <div className={`font-mono text-sm font-bold px-3 py-1 rounded-full ${warning?'bg-rose-100 text-rose-700 animate-pulse':'bg-amber-100 text-amber-700'}`} data-testid="exam-timer">
          <Clock className="h-3 w-3 inline mr-1"/> {String(min).padStart(2,'0')}:{String(sec).padStart(2,'0')}
        </div>
      </div>
      <h2 className="text-base font-bold text-gray-900">{attempt.exam_title}</h2>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">Q {cur+1}/{questions.length} · {answeredCount} answered · {Object.values(flagged).filter(Boolean).length} flagged</div>
      {/* Progress */}
      <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-amber-500" style={{width: `${(answeredCount/questions.length)*100}%`}}/>
      </div>
      {/* Navigator */}
      <div className="mt-3 flex flex-wrap gap-1" data-testid="exam-navigator">
        {questions.map((qq,i) => (
          <button key={qq.id} onClick={()=>setCur(i)}
            className={`w-7 h-7 rounded text-[10px] font-bold ${i===cur?'bg-amber-600 text-white':answers[qq.id]!==undefined&&answers[qq.id]!==''?'bg-emerald-100 text-emerald-800':'bg-gray-100 text-gray-600 hover:bg-gray-200'} ${flagged[qq.id]?'ring-2 ring-rose-400':''}`}
            data-testid={`exam-nav-${i}`}>{i+1}</button>
        ))}
      </div>
      {/* Current Q */}
      <div className="mt-4 border border-gray-200 rounded-xl p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="text-sm font-medium flex-1">{cur+1}. {q.prompt}</div>
          <button onClick={flag} className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded inline-flex items-center gap-1 ${flagged[q.id]?'bg-rose-100 text-rose-700':'bg-gray-100 text-gray-600'}`} data-testid="exam-flag"><Flag className="h-2.5 w-2.5"/>{flagged[q.id]?'Unflag':'Flag'}</button>
        </div>
        {q.choices && (
          <div className="mt-2 grid sm:grid-cols-2 gap-1.5">
            {q.choices.map((c,ci) => (
              <label key={ci} className={`text-xs border rounded-lg px-2.5 py-1.5 cursor-pointer ${answers[q.id]===ci?'border-amber-500 bg-amber-50':'border-gray-200 hover:bg-gray-50'}`}>
                <input type="radio" name={q.id} className="hidden" checked={answers[q.id]===ci} onChange={()=>setAnswers(p=>({...p,[q.id]:ci}))}/>
                {String.fromCharCode(65+ci)}. {c}
              </label>
            ))}
          </div>
        )}
        {q.kind === 'writing' && (
          <textarea rows={6} value={answers[q.id]||''} placeholder="Type your response…" className="mt-2 w-full text-sm border rounded-lg p-2" onChange={e=>setAnswers(p=>({...p,[q.id]:e.target.value}))} data-testid="exam-writing"/>
        )}
      </div>
      {/* Bottom nav */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          <button onClick={()=>setCur(Math.max(0, cur-1))} disabled={cur===0} className="text-xs bg-gray-100 hover:bg-gray-200 disabled:opacity-40 px-3 py-1.5 rounded-full inline-flex items-center gap-1" data-testid="exam-prev"><ChevronLeft className="h-3 w-3"/> Previous</button>
          <button onClick={skip} className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-full inline-flex items-center gap-1" data-testid="exam-skip">Skip</button>
          <button onClick={()=>setCur(Math.min(questions.length-1, cur+1))} disabled={cur===questions.length-1} className="text-xs bg-gray-100 hover:bg-gray-200 disabled:opacity-40 px-3 py-1.5 rounded-full inline-flex items-center gap-1" data-testid="exam-next">Next <ChevronRight className="h-3 w-3"/></button>
        </div>
        <button onClick={submit} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-50" data-testid="exam-submit">
          {busy?<Loader2 className="h-3 w-3 animate-spin"/>:<Send className="h-3 w-3"/>} Submit Exam
        </button>
      </div>
    </div>
  );
};

const Results = ({ result, onClose }) => {
  return (
    <div className="space-y-4" data-testid="exam-results">
      <div className="bg-gradient-to-br from-amber-500 to-amber-700 text-white rounded-2xl p-5">
        <div className="text-[10px] uppercase tracking-[3px] text-white/80 font-bold">Examination Complete</div>
        <h2 className="text-xl font-bold mt-1">{result.exam_title}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
          <Big label="Overall Score" value={`${result.score_pct ?? 0}%`} primary/>
          {result.maths_score_pct!==null && result.maths_score_pct!==undefined && <Big label="Maths Score" value={`${result.maths_score_pct}%`}/>}
          {result.english_score_pct!==null && result.english_score_pct!==undefined && <Big label="English Score" value={`${result.english_score_pct}%`}/>}
          <Big label="Time" value={`${Math.round((result.duration_seconds||0)/60)} min`}/>
          <Big label="Avg time/q" value={`${result.avg_time_seconds}s`}/>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <MiniCard label="Correct" value={result.correct} accent="emerald"/>
        <MiniCard label="Incorrect" value={(result.auto_gradable||0)-(result.correct||0)-(result.skipped||0)} accent="rose"/>
        <MiniCard label="Skipped" value={result.skipped||0} accent="amber"/>
      </div>

      {result.ai_report && (
        <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid="exam-ai-report">
          <div className="text-[10px] uppercase tracking-[2px] text-red-700 font-bold flex items-center gap-1"><Sparkles className="h-3 w-3"/> AI Performance Report</div>
          <div className="mt-2 text-sm text-gray-800 whitespace-pre-wrap">{result.ai_report}</div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <BreakdownList title="Weakest topics" rows={result.weakest_topics || []} testid="weakest" tone="rose"/>
        <BreakdownList title="Strongest topics" rows={result.strongest_topics || []} testid="strongest" tone="emerald"/>
      </div>

      {result.recommended_topics?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid="adaptive-recommendations">
          <div className="text-sm font-bold flex items-center gap-2 mb-2"><Target className="h-4 w-4 text-amber-600"/> Adaptive revision</div>
          <div className="text-xs text-gray-700">Recommended topics: {result.recommended_topics.map(t => <span key={t} className="inline-block bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full mr-1 mb-1">{t}</span>)}</div>
          {result.recommended_resources?.length > 0 && <div className="text-xs text-gray-700 mt-1">Recommended resources: {result.recommended_resources.map(t => <span key={t} className="inline-block bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full mr-1 mb-1">{t}</span>)}</div>}
          {result.next_exam_slug && <div className="text-xs text-gray-700 mt-1">Suggested next exam: <strong>{result.next_exam_slug}</strong></div>}
        </div>
      )}

      <details className="bg-white border border-gray-200 rounded-2xl p-4">
        <summary className="text-sm font-bold cursor-pointer">Review answers ({(result.per_question||[]).length})</summary>
        <div className="mt-2 space-y-1.5 text-xs">
          {(result.per_question||[]).map((pq,i) => (
            <div key={i} className={`border rounded p-2 ${pq.correct===false?'border-rose-200 bg-rose-50':pq.correct===null?'border-gray-200 bg-gray-50':'border-emerald-200 bg-emerald-50'}`}>
              <div className="font-bold">Q{i+1}: {pq.prompt.slice(0,140)}</div>
              {pq.explanation && <div className="text-gray-700 mt-1">{pq.explanation}</div>}
            </div>
          ))}
        </div>
      </details>

      <div className="flex justify-end">
        <button onClick={onClose} className="bg-amber-600 hover:bg-amber-700 text-white px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider" data-testid="exam-back-to-centre">Back to Examination Centre</button>
      </div>
    </div>
  );
};

const Big = ({label, value, primary}) => (
  <div className={`rounded-xl p-3 ${primary?'bg-white/30':'bg-white/15'}`}>
    <div className="text-[10px] uppercase tracking-wider font-bold text-white/80">{label}</div>
    <div className="text-xl font-bold mt-0.5">{value}</div>
  </div>
);
const MiniCard = ({label, value, accent}) => (
  <div className={`rounded-xl p-3 border bg-${accent}-50 border-${accent}-200`}>
    <div className={`text-[10px] uppercase tracking-wider font-bold text-${accent}-700`}>{label}</div>
    <div className="text-2xl font-bold text-gray-900 mt-0.5">{value}</div>
  </div>
);
const BreakdownList = ({title, rows, testid, tone}) => (
  <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid={testid}>
    <div className="text-sm font-bold mb-2">{title}</div>
    {rows.length === 0 && <div className="text-xs italic text-gray-400">No data</div>}
    <div className="space-y-1">
      {rows.map((r,i) => (
        <div key={i} className="flex justify-between text-xs">
          <span>{r.topic_slug}</span>
          <span className={`font-bold ${tone==='rose'?'text-rose-700':'text-emerald-700'}`}>{r.pct}% ({r.correct}/{r.total})</span>
        </div>
      ))}
    </div>
  </div>
);

export default ExamCentrePage;
